import React from 'react';
import {useTranslation} from 'react-i18next';

import isFunction from 'lodash/isFunction';
import unionBy from 'lodash/unionBy';

import {THead} from '../Dropdown/types';
import TextInput from '../TextInput';
import * as S from './style';
import {EGroup, TEmoji} from './types';
import {
  ALL_EMOJIS,
  GROUPS,
  getEmojiGroupHash,
  getRecent,
  groupIconHash,
  updateRecent,
} from './utils';

export type TProps = {
  className?: string;
  onSelect: (char: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  head?: THead;
};

const scrollTo = (element: HTMLElement, targetScrollTop: number) => {
  if (isFunction(element.scrollTo)) {
    element.scrollTo({behavior: 'smooth', top: targetScrollTop});
  } else {
    // eslint-disable-next-line no-param-reassign
    element.scrollTop = targetScrollTop;
  }
};

const useGroupsScroll = (onChange: (group: EGroup) => void) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const groupRefs = React.useRef<Partial<Record<EGroup, HTMLDivElement>>>({});
  const targetScrollTopRef = React.useRef<number | null>(null);

  const makeGroupRef = React.useCallback(
    (group: EGroup) => (element: HTMLDivElement) => {
      groupRefs.current[group] = element;
    },
    [],
  );

  const isCustomScrollEnded = React.useCallback(() => {
    const {current: contentNode} = contentRef;

    return (
      contentNode === null ||
      targetScrollTopRef.current === null ||
      targetScrollTopRef.current === contentNode.scrollTop
    );
  }, []);

  const handleScroll = React.useCallback(() => {
    const {current: contentNode} = contentRef;

    if (contentNode === null || !isCustomScrollEnded()) {
      return;
    }

    targetScrollTopRef.current = null;

    const contentRect = contentNode.getBoundingClientRect();
    const nextActiveGroup = [...GROUPS].reverse().find((group) => {
      const element = groupRefs.current[group];

      if (element === undefined) {
        return false;
      }
      const elementRect = element.getBoundingClientRect();

      return elementRect.top - contentRect.top <= 50;
    });

    if (nextActiveGroup) {
      onChange(nextActiveGroup);
    }
  }, [isCustomScrollEnded, onChange]);

  const scrollToGroup = React.useCallback(
    (group: EGroup) => {
      const {current: contentNode} = contentRef;
      const elementNode = groupRefs.current[group];

      if (contentNode === null || elementNode === undefined) {
        return;
      }

      const contentRect = contentNode.getBoundingClientRect();
      const elementRect = elementNode.getBoundingClientRect();
      const targetScrollTop = elementRect.top - contentRect.top + contentNode.scrollTop;

      onChange(group);
      targetScrollTopRef.current = targetScrollTop;
      scrollTo(contentNode, targetScrollTop);
    },
    [onChange],
  );

  return {
    handleScroll,
    scrollToGroup,
    contentRef,
    makeGroupRef,
  };
};

const useRecentEmojiStorage = () => {
  const addRecentEmoji = React.useCallback((emoji) => {
    const recent = getRecent();
    const isRecentEmoji = (recentEmoji: TEmoji) => recentEmoji.char === emoji.char;

    if (recent.find(isRecentEmoji) === undefined) {
      updateRecent(unionBy([emoji, ...recent], 'name'));
    }
  }, []);

  return [addRecentEmoji];
};

const useEmojiFilter = (): [string, (filter: string) => void, TEmoji[]] => {
  const [filter, setFilter] = React.useState<string>('');

  const filteredEmojis = React.useMemo(() => {
    if (filter === '') {
      return [];
    }

    return ALL_EMOJIS.filter((emoji) =>
      emoji.keywords.some((keyword) => keyword.indexOf(filter) >= 0),
    );
  }, [filter]);

  return [filter, setFilter, filteredEmojis];
};

type TEmojiPickerContentProps = {
  onSelect: TProps['onSelect'];
  ref?: any;
  unbounded?: boolean;
};

const EmojiPickerContent = React.memo<TEmojiPickerContentProps>(
  ({onSelect, ref, unbounded = false}) => {
    const {t} = useTranslation();
    const [activeGroup, setActiveGroup] = React.useState<EGroup>(EGroup.recent);

    const [filter, updateFilter, filteredEmojis] = useEmojiFilter();
    const hasFilter = filter !== '';

    const {handleScroll, scrollToGroup, contentRef, makeGroupRef} = useGroupsScroll(setActiveGroup);

    const [addRecentEmoji] = useRecentEmojiStorage();

    const handleChangeFilter = React.useCallback(
      (event) => updateFilter(event.target.value),
      [updateFilter],
    );

    const handleSelectChar = React.useCallback(
      (emoji: TEmoji) => {
        addRecentEmoji(emoji);
        onSelect(emoji.char);
      },
      [addRecentEmoji, onSelect],
    );

    React.useEffect(() => {
      if (hasFilter) {
        setActiveGroup(EGroup.recent);
      }
    }, [hasFilter]);

    const groupHash = getEmojiGroupHash();

    const pointerPosition = React.useMemo(
      () => GROUPS.findIndex((group) => group === activeGroup),
      [activeGroup],
    );

    const renderHeadGroup = (group: EGroup) => {
      const handleClick = () => scrollToGroup(group);
      const isActive = !hasFilter && group === activeGroup;

      return (
        <S.HeadButton
          key={group}
          isActive={isActive}
          disabled={hasFilter}
          onClick={hasFilter ? undefined : handleClick}
        >
          {<S.Icon as={groupIconHash[group]} />}
        </S.HeadButton>
      );
    };

    const renderEmoji = (emoji: TEmoji) => {
      return (
        <S.Char key={emoji.name} onClick={() => handleSelectChar(emoji)}>
          {emoji.char}
        </S.Char>
      );
    };

    const renderGroup = (group: EGroup) => {
      const isRecent = group === EGroup.recent;
      const emojis = groupHash[group];
      const isEmptyRecent = isRecent && emojis.length === 0;

      return (
        <div key={group} ref={makeGroupRef(group)}>
          <S.GroupName>
            {t([`EdnaEmojiPicker:group.${group}`, `EmojiPicker:group.${group}`])}
          </S.GroupName>
          <S.Symbols>
            {isEmptyRecent && (
              <S.Secondary>{t(['EdnaEmojiPicker:empty', 'EmojiPicker:empty'])}</S.Secondary>
            )}
            {emojis.map(renderEmoji)}
          </S.Symbols>
        </div>
      );
    };

    return (
      <S.EmojiPicker unbounded={unbounded}>
        <S.Header>
          {GROUPS.map(renderHeadGroup)}
          {!hasFilter && <S.HeaderPointer position={pointerPosition} counter={GROUPS.length} />}
        </S.Header>
        <S.Search>
          <TextInput
            innerRef={ref}
            isEnableReset
            placeholder={t(['EdnaEmojiPicker:search', 'EmojiPicker:search'])}
            value={filter}
            onChange={handleChangeFilter}
          />
        </S.Search>
        <S.Content onScroll={hasFilter ? undefined : handleScroll} ref={contentRef}>
          {hasFilter && (
            <>
              <S.GroupName>
                {t(['EdnaEmojiPicker:searchResult', 'EmojiPicker:searchResult'])}
              </S.GroupName>
              <S.Symbols>
                {filteredEmojis.length === 0 && (
                  <S.Secondary>
                    {t(['EdnaEmojiPicker:notFound', 'EmojiPicker:notFound'])}
                  </S.Secondary>
                )}
                {filteredEmojis.map(renderEmoji)}
              </S.Symbols>
            </>
          )}
          {!hasFilter && GROUPS.map(renderGroup)}
        </S.Content>
      </S.EmojiPicker>
    );
  },
);

const EmojiPicker = React.memo<TProps>(({className, onSelect, disabled, readOnly, head}) => {
  const {t} = useTranslation();

  const renderHead = React.useCallback(
    ({isOpen}) => (
      <S.HeadButton
        className={className}
        isActive={isOpen}
        title={t(['EdnaEmojiPicker:select', 'EmojiPicker:select'])}
        disabled={disabled || readOnly}
      >
        <S.SmileIcon />
      </S.HeadButton>
    ),
    [readOnly, className, disabled, t],
  );

  return (
    <S.Dropdown
      closeWhen="clickOutsideOverlay"
      head={head ?? renderHead}
      overlay={<EmojiPickerContent onSelect={onSelect} />}
      headComponent={S.DropdownHeader}
      readOnly={readOnly}
      disabled={disabled}
    />
  );
});

export {EmojiPickerContent};

export default EmojiPicker;
